/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 4: a Gym settings card. Read in plain words, edit in place, save
 * or cancel. A failed save keeps the form open with what was typed.
 */
import React, { useEffect, useRef, useState } from "react";
import { IconPencil } from "@tabler/icons-react";
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@lemonadetraining/web-ui";

export interface SettingsCardProps {
  id: string;
  title: string;
  description: string;
  /** The read view. */
  children: React.ReactNode;
  /** The edit form; omit for cards that act in place (switches, rooms). */
  renderEdit?: () => React.ReactNode;
  /** Runs the save; throw to keep the form open (an error's `userMessage` is shown). Return false to stay in edit (focus goes to the first invalid field, else Save). */
  onSave?: () => Promise<boolean | void>;
  onStartEdit?: () => void;
  onCancel?: () => void;
  saveLabel?: string;
  /** After this long a save still running re-enables Cancel and Save and shows `slowMessage`. */
  slowAfterMs?: number;
  slowMessage?: string;
  /** Save's label after a save failed or ran slow. */
  retryLabel?: string;
}

export function SettingsCard({
  id,
  title,
  description,
  children,
  renderEdit,
  onSave,
  onStartEdit,
  onCancel,
  saveLabel = "Save",
  slowAfterMs,
  slowMessage,
  retryLabel,
}: SettingsCardProps) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(0);
  const [slow, setSlow] = useState(false);
  const [attempted, setAttempted] = useState(false);
  // A save settling after a newer save, or after Cancel/Edit, must not touch the card.
  const saveSeq = useRef(0);
  const session = useRef(0);
  const slowTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editButton = useRef<HTMLButtonElement>(null);
  const saveButton = useRef<HTMLButtonElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const wasEditing = useRef(false);

  // The Edit and footer buttons unmount on a switch, so focus moves with it.
  useEffect(() => {
    if (editing) {
      content.current
        ?.querySelector<HTMLElement>(
          "input:not([type=file]), textarea, button[role=combobox], [data-picker-focus]"
        )
        ?.focus();
    } else if (wasEditing.current) {
      editButton.current?.focus();
    }
    wasEditing.current = editing;
  }, [editing]);

  useEffect(() => {
    if (!blocked) return;
    const invalid = content.current?.querySelector<HTMLElement>("[aria-invalid=true]");
    (invalid ?? saveButton.current)?.focus();
  }, [blocked]);

  const stopSlowTimer = () => {
    if (slowTimer.current) clearTimeout(slowTimer.current);
    slowTimer.current = null;
  };
  useEffect(() => stopSlowTimer, []);

  const save = async () => {
    if (!onSave) return;
    const seq = ++saveSeq.current;
    const at = session.current;
    const stale = () => seq !== saveSeq.current || at !== session.current;
    setSaving(true);
    setFailed(null);
    setSlow(false);
    stopSlowTimer();
    if (slowAfterMs !== undefined) {
      slowTimer.current = setTimeout(() => {
        setSlow(true);
        setAttempted(true);
      }, slowAfterMs);
    }
    try {
      const ok = await onSave();
      if (stale()) return;
      if (ok === false) setBlocked((n) => n + 1);
      else setEditing(false);
    } catch (error: unknown) {
      if (stale()) return;
      setAttempted(true);
      const code = (error as { code?: string })?.code ?? "";
      const userMessage = (error as { userMessage?: string })?.userMessage;
      setFailed(
        userMessage
          ? userMessage
          : code.includes("permission-denied")
            ? "Only the gym's owners and admins can change this."
            : code.includes("invalid-argument")
              ? ((error as { message?: string }).message ?? "Something in the form isn't valid.")
              : "Couldn't save. Nothing changed. Try again."
      );
    } finally {
      if (!stale()) {
        stopSlowTimer();
        setSlow(false);
        setSaving(false);
      }
    }
  };

  const busy = saving && !slow;
  const alert = failed ?? (slow && slowMessage ? slowMessage : null);

  return (
    <Card data-testid={`settings-card-${id}`}>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
        {renderEdit && !editing && (
          <CardAction>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                session.current += 1;
                onStartEdit?.();
                setFailed(null);
                setAttempted(false);
                setEditing(true);
              }}
              ref={editButton}
              aria-label={`Edit ${title}`}
              data-testid={`settings-edit-${id}`}
            >
              <IconPencil aria-hidden="true" />
              Edit
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div ref={content} className="contents">
          {editing && renderEdit ? renderEdit() : children}
        </div>
        {alert && (
          <Alert variant="destructive" data-testid={`settings-error-${id}`}>
            <AlertDescription>{alert}</AlertDescription>
          </Alert>
        )}
      </CardContent>
      {editing && (
        <CardFooter className="justify-end gap-2 border-t pt-4">
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            onClick={() => {
              session.current += 1;
              stopSlowTimer();
              setSlow(false);
              setSaving(false);
              setFailed(null);
              setEditing(false);
              onCancel?.();
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            ref={saveButton}
            disabled={busy}
            onClick={() => void save()}
            data-testid={`settings-save-${id}`}
          >
            {busy ? "Saving…" : retryLabel && attempted ? retryLabel : saveLabel}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}

/** A labelled read-only value. */
export function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="break-words font-medium">{children}</dd>
    </div>
  );
}
